import React from 'react';

import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { ResizeCallbackData } from 'react-resizable';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import { NSKeys } from '@app/i18n';
import { WAREHOUSE_ACCEPTANCE_TABLE_COLUMNS, WAREHOUSE_SHIPMENTS_TABLE_COLUMNS } from '@app/services/appSettings';
import { useTableParams } from '@app/services/hooks/useTableParams';
import { TableDnDResizable } from '@components/TableDnDResizable';

import { useGetWarehouseDocumentsQuery } from '../../../../hooks';

import { DocumentDetailDrawer } from './components/DocumentDetailDrawer';
import { SearchControls, useWarehouseDocumentSearchParams } from './components/SearchControls';
import { RECEIPT_COLUMNS, SHARED_COLUMNS, SHIPMENT_COLUMNS } from './constants';
import { RetrieveDocumentListItemSchema, WarehouseDocumentKind } from './interfaces';

interface IProps {
  kind: WarehouseDocumentKind;
}

export const WarehouseDocuments: React.FC<IProps> = ({ kind }) => {
  const params = useParams<{ id: string }>();
  const documentsT = useTranslation(NSKeys.warehouseDocuments);
  const commonT = useTranslation(NSKeys.common);
  const { current, pageSize, setPagination } = useTableParams();
  const [selectedDocumentUid, setSelectedDocumentUid] = React.useState<string | null>(null);
  const { searchValue, statusValue, recipientValue, senderValue } = useWarehouseDocumentSearchParams();

  const { data, isLoading } = useGetWarehouseDocumentsQuery({
    warehouse_uid: params.id || '',
    kind,
    offset: (current - 1) * pageSize,
    limit: pageSize,
    q: searchValue || '',
    status: statusValue,
    recipient: recipientValue,
    sender: senderValue,
  });

  const sharedColumns = React.useMemo<ColumnsType<RetrieveDocumentListItemSchema>>(
    () =>
      SHARED_COLUMNS.map((column) => ({
        ...column,
        title: documentsT.t(`tableColumns.${String(column.key)}` as any),
      })),
    [documentsT]
  );

  const shipmentColumns = React.useMemo<ColumnsType<RetrieveDocumentListItemSchema>>(
    () =>
      SHIPMENT_COLUMNS.map((column) => ({
        ...column,
        title: documentsT.t(`tableColumns.${String(column.key)}` as any),
      })),
    [documentsT]
  );

  const receiptColumns = React.useMemo<ColumnsType<RetrieveDocumentListItemSchema>>(
    () =>
      RECEIPT_COLUMNS.map((column) => ({
        ...column,
        title: documentsT.t(`tableColumns.${String(column.key)}` as any),
      })),
    [documentsT]
  );

  const columns = React.useMemo<ColumnsType<RetrieveDocumentListItemSchema>>(
    () => (kind === 'shipment' ? [...sharedColumns, ...shipmentColumns] : [...sharedColumns, ...receiptColumns]),
    [kind, sharedColumns, shipmentColumns, receiptColumns]
  );

  const storageKey = `${params.id || 'warehouse'}${
    kind === 'shipment' ? WAREHOUSE_SHIPMENTS_TABLE_COLUMNS : WAREHOUSE_ACCEPTANCE_TABLE_COLUMNS
  }`;
  const [localStorageColumns, setLocalStorageColumns] = useLocalStorage(
    storageKey,
    columns.map(({ render, ...rest }) => rest)
  );
  const totalTableWidth = React.useMemo(
    () => localStorageColumns.reduce((sum, column) => sum + Number(column.width || 0), 0),
    [localStorageColumns]
  );

  const handleResize =
    (index: number) =>
    (_: React.SyntheticEvent<Element>, { size }: ResizeCallbackData) => {
      const newColumns = [...localStorageColumns];
      newColumns[index] = {
        ...newColumns[index],
        width: size.width > 20 ? size.width : 20,
      };
      setLocalStorageColumns(newColumns);
    };

  const emptyText = kind === 'shipment' ? documentsT.t('emptyShipments') : documentsT.t('emptyReceipts');

  return (
    <>
      <SearchControls disabled={isLoading} kind={kind} warehouseUid={params.id || ''} />
      <TableDnDResizable<RetrieveDocumentListItemSchema>
        localStorageName={storageKey}
        dataSource={data?.items}
        rowKey="uid"
        className="tableClassname"
        columns={localStorageColumns.map((column, index) => ({
          ...column,
          onHeaderCell: (column: any) => ({
            width: column.width,
            onResize: handleResize(index) as React.ReactEventHandler<any>,
          }),
          render: columns.find((baseColumn) => baseColumn.key === column.key)?.render,
        }))}
        onColumnsChange={setLocalStorageColumns}
        loading={isLoading}
        tableLayout="fixed"
        scroll={{ x: totalTableWidth }}
        locale={{ emptyText }}
        onRow={(record) => ({
          onClick: () => setSelectedDocumentUid(record.uid),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current,
          pageSize,
          total: data?.total,
          showQuickJumper: true,
          showSizeChanger: true,
          position: ['bottomCenter'],
          onChange: (newPage, newSize) => {
            setPagination(newPage, newSize);
          },
          showTotal: (total, range) => `${range[0]}-${range[1]} ${commonT.t('paginationOf')} ${total}`,
        }}
      />
      <DocumentDetailDrawer
        documentUid={selectedDocumentUid}
        open={!!selectedDocumentUid}
        onClose={() => setSelectedDocumentUid(null)}
      />
    </>
  );
};
